import React from "react";
import { Flex, Text, Select, HelpIndicator } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { VerificationStatus } from "../types";
import { FIELD_CONTAINER_STYLE, VERIFICATION_OPTIONS } from "./constants";
import { VerificationStatusSelectProps } from "./types";

/**
 * VerificationStatusSelect component renders a dropdown for selecting
 * a bank verification status with a help icon.
 *
 * @component
 * @example
 * ```tsx
 * <VerificationStatusSelect
 *   value={fidesVerificationStatus}
 *   onChange={handleVerificationChange}
 *   testId="fides-verification-select"
 *   width="40%"
 * />
 * ```
 */
export const VerificationStatusSelect: React.FC<VerificationStatusSelectProps> = ({
  value,
  onChange,
  testId,
  width = "100%",
}) => {
  const handleChange = (val: string | number | (string | number)[]) => {
    onChange(val as VerificationStatus);
  };

  return (
    <div style={{ ...FIELD_CONTAINER_STYLE, width }}>
      <Flex alignItems="center" gap={4}>
        <Text variant="strong-sm">Bank verification status</Text>
        <HelpIndicator
          id="bank-verification-status-tooltip"
          placement="top"
          content="Use Unverified for UAT testing; once testing is successful, you must manually switch the status to Verified for production use"
        >
          <Icon
            type="question-circle"
            size="small"
            variant="outline"
            style={{ color: "#9CA3AF", cursor: "pointer" }}
          />
        </HelpIndicator>
      </Flex>
      <Select
        placeholder="Select status"
        value={value}
        onChange={handleChange}
        full
        data-testid={testId}
      >
        <Select.Section>
          {VERIFICATION_OPTIONS.map((option) => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select.Section>
      </Select>
    </div>
  );
};
